import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material";
import SwitchToggle from "../../components/SwitchToggle";
import Pagination from "../../components/Panigation";
import { useUpdateShowroomMutation, useDeleteShowroomMutation } from "../../service/showroom/api";

interface DealerListProps {
  showrooms: any[];
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  paginator: any;
  isLoading: boolean;
}

const DealerList = ({ showrooms, page, setPage, paginator, isLoading }: DealerListProps) => {
  const navigate = useNavigate();
  const [updateShowroom, { isLoading: isUpdating }] = useUpdateShowroomMutation();
  const [deleteShowroom, { isLoading: isDeleting }] = useDeleteShowroomMutation();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleToggleActive = async (row: any) => {
    try {
      await updateShowroom({ id: row._id, isActive: !row.isActive }).unwrap();
      toast.success(
        `Showroom ${!row.isActive ? "enabled" : "disabled"} successfully`,
        { autoClose: 2000 }
      );
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteShowroom(deleteId).unwrap();
      toast.success("SHOWROOM_DELETED_SUCCESSFULLY");
      setDeleteId(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete showroom");
    }
  };

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>تفاصيل المعرض / Showroom Detail</TableCell>
              <TableCell>الموقع / Location</TableCell>
              <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
              <TableCell>إجراء / Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : showrooms.length > 0 ? (
              showrooms.map((showroom: any) => (
                <TableRow hover key={showroom._id}>
                  {/* SHOWROOM DETAIL — logo, name, phone, email */}
                  <TableCell>
                    <div className="user_block">
                      <figure className="no_radius">
                        <img
                          src={showroom.logo || "/static/images/placeholder.png"}
                          alt="Showroom"
                        />
                      </figure>
                      <p>
                        <b>{showroom.name || "N/A"}</b>
                        <span>{showroom.phone || "N/A"}</span>
                        <span>{showroom.email || "N/A"}</span>
                      </p>
                    </div>
                  </TableCell>

                  {/* LOCATION */}
                  <TableCell>{showroom.address || "N/A"}</TableCell>

                  {/* ENABLE/DISABLE TOGGLE */}
                  <TableCell>
                    <SwitchToggle
                      checked={showroom.isActive}
                      onChange={() => handleToggleActive(showroom)}
                      disabled={isUpdating}
                    />
                  </TableCell>

                  {/* ACTION */}
                  <TableCell>
                    <div className="table_actions v2">
                      <Tooltip title="View" arrow>
                        <IconButton
                          onClick={() => navigate(`/manage-dealers/detail/${showroom._id}`)}
                        >
                          <img src="/static/images/eye_icon.svg" alt="View" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          onClick={() => navigate(`/manage-dealers/edit/${showroom._id}`)}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton onClick={() => setDeleteId(showroom._id)}>
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  لا توجد بيانات / No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {paginator?.itemCount > 10 && (
        <Pagination
          module={showrooms}
          page={page}
          setPage={setPage}
          onPageChange={(newP: number) => setPage(newP)}
          totalPages={paginator?.pageCount || 1}
          totalCount={paginator?.itemCount || 0}
        />
      )}

      <Dialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this dealer/showroom? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} color="primary" disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={isDeleting} variant="contained">
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DealerList;
